// Shape of a "save this matrix to the workspace" request.
//
// Both output matrices publish: the Modeled grid saves a `modeled` plan and the
// Final Staff Plan card saves a `final` one. The page that owns the client fills
// in the rest of the provenance (scenario id, name, model, timestamp), so this
// only carries what the matrix itself knows.

import type {
  StaffPlan,
  StaffPlanCell,
  StaffPlanHorizon,
  StaffPlanKind,
  StaffPlanSource,
} from '@rc/engine/types';

/** Provenance the publishing matrix knows; the caller supplies the rest. */
export type PublishPlanProvenance = Partial<
  Omit<StaffPlanSource, 'scenarioId' | 'scenarioName' | 'modelId' | 'generatedAt'>
>;

export interface PublishPlanArgs {
  name: string;
  kind: StaffPlanKind;
  cells: StaffPlanCell[];
  source: PublishPlanProvenance;
  /** Set to overwrite an existing saved plan instead of creating a new one. */
  replaceId?: string;
}

/** A saved plan a matrix could overwrite, as much of it as the button needs. */
export interface PublishTarget {
  id: string;
  name: string;
  updatedAt?: string;
}

/** Looks up the plan a matrix would overwrite. Supplied by the page that owns
 *  the client's plan list, so the matrices need not know about it. */
export type FindPublishedPlan = (
  kind: StaffPlanKind,
  horizon?: StaffPlanHorizon,
) => PublishTarget | undefined;

/**
 * The plan a matrix's "Update" action should overwrite: the most recently
 * touched plan this scenario has already published of the same kind.
 *
 * A horizon is part of what identifies a Final plan — Year 2 and Year 3 off the
 * same scenario are two different answers, so re-saving Year 3 must not overwrite
 * the Year 2 plan. For a Modeled plan there is no horizon and the kind alone
 * identifies it.
 */
export function findPublishedPlan(
  plans: Pick<StaffPlan, 'id' | 'name' | 'kind' | 'source' | 'updatedAt'>[],
  scenarioId: string,
  kind: StaffPlanKind,
  horizon?: StaffPlanHorizon,
): PublishTarget | undefined {
  const matches = plans.filter(
    (p) =>
      p.kind === kind &&
      p.source?.scenarioId === scenarioId &&
      (kind !== 'final' || (p.source?.horizon || 'full') === (horizon || 'full')),
  );
  if (matches.length === 0) return undefined;

  const latest = matches.reduce((best, p) =>
    (p.updatedAt || '') > (best.updatedAt || '') ? p : best,
  );
  return { id: latest.id, name: latest.name, updatedAt: latest.updatedAt };
}
